// Bespoke SVG chart for note figures (figures mockup): grouped bars or lines, hover tooltip,
// legend for ≥2 series. Series palette #7a68f0/#22a3b6 is CVD-validated (spec §7) — the only
// literal colors allowed in the note viewer; axes/grid use theme tokens via CSS classes.
// Guard (spec §7): a chart whose series lengths don't match x_labels (or with non-finite values)
// renders as a table instead — never a broken/misleading SVG. Negative values are RENDERED, not
// rejected: the domain spans 0 in both directions and bars measure from the zero baseline.

import { useState } from 'react';
import type { NoteChart } from '../../lib/note-doc';
import { NoteTableView } from './NoteTable';

const SERIES_COLORS = ['#7a68f0', '#22a3b6'];

export function chartIsRenderable(chart: NoteChart): boolean {
  return chart.x_labels.length > 0 && chart.series.length > 0 &&
    chart.series.every(s => s.values.length === chart.x_labels.length && s.values.every(v => Number.isFinite(v)));
}

function chartAsTable(chart: NoteChart) {
  return {
    caption: chart.caption,
    columns: ['', ...chart.series.map(s => s.name)],
    rows: chart.x_labels.map((label, i) => [label, ...chart.series.map(s => String(s.values[i] ?? ''))]),
  };
}

export function NoteChartView({ chart }: { chart: NoteChart }) {
  const [tip, setTip] = useState<{ x: number; y: number; text: string } | null>(null);
  if (!chartIsRenderable(chart)) {
    return <NoteTableView table={chartAsTable(chart)} />;
  }
  const W = 600, H = 220, X0 = 50, X1 = 590, Y0 = 20, Y1 = 190;
  // The domain always spans 0 so bars measure from a real baseline. Grounded figures carry
  // verbatim source numbers, which include negatives (a latency delta, a -52% change); a
  // 0..max domain mapped those below the plot and produced rect height="-N", which SVG drops.
  // For all-positive data `lo` collapses to 0 and every coordinate is identical to before.
  const values = chart.series.flatMap(s => s.values);
  const hi = Math.max(...values, 0) * 1.1;
  const lo = Math.min(...values, 0) * 1.1;
  const span = (hi - lo) || 1;
  const y = (v: number) => Y1 - ((v - lo) / span) * (Y1 - Y0);
  const baseline = y(0);
  const groupW = (X1 - X0) / chart.x_labels.length;
  const color = (i: number) => SERIES_COLORS[i % SERIES_COLORS.length];
  const dim = (i: number) => (i >= SERIES_COLORS.length ? 0.6 : 1);
  return (
    <figure className="nd-fig">
      <figcaption className="nd-fig-cap"><b>Chart</b> · {chart.caption}</figcaption>
      {chart.series.length >= 2 && (
        <div className="nd-legend">
          {chart.series.map((s, i) => (
            <span key={i}><span className="nd-sw" style={{ background: color(i), opacity: dim(i) }} />{s.name}</span>
          ))}
        </div>
      )}
      <div className="nd-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} className="nd-chart" onMouseLeave={() => setTip(null)}>
          {[1 / 3, 2 / 3, 1].map((f, i) => {
            const gy = Y1 - f * (Y1 - Y0);
            return (
              <g key={i}>
                <line x1={X0} y1={gy} x2={X1} y2={gy} className="nd-grid" />
                <text x={X0 - 6} y={gy + 4} textAnchor="end" className="nd-axis">{(lo + f * span).toPrecision(2)}</text>
              </g>
            );
          })}
          <line x1={X0} y1={baseline} x2={X1} y2={baseline} className="nd-grid nd-grid-base" />
          {chart.kind === 'bar'
            ? chart.x_labels.map((_, li) => {
                const n = chart.series.length;
                const barW = Math.min(24, (groupW * 0.7) / n);
                const start = X0 + groupW * li + (groupW - barW * n) / 2;
                return chart.series.map((s, si) => (
                  <rect key={`${li}-${si}`} x={start + si * barW} y={Math.min(y(s.values[li]), baseline)}
                    width={Math.max(barW - 2, 1)} height={Math.abs(baseline - y(s.values[li]))} rx={2}
                    fill={color(si)} opacity={dim(si)}
                    onMouseEnter={() => setTip({ x: start + si * barW, y: y(s.values[li]), text: `${s.name} · ${chart.x_labels[li]}: ${s.values[li]}` })}>
                    <title>{`${s.name} · ${chart.x_labels[li]}: ${s.values[li]}`}</title>
                  </rect>
                ));
              })
            : chart.series.map((s, si) => (
                <g key={si}>
                  <polyline fill="none" stroke={color(si)} strokeWidth={2} opacity={dim(si)}
                    points={s.values.map((v, li) => `${X0 + groupW * (li + 0.5)},${y(v)}`).join(' ')} />
                  {s.values.map((v, li) => (
                    <circle key={li} cx={X0 + groupW * (li + 0.5)} cy={y(v)} r={3.5} fill={color(si)}
                      onMouseEnter={() => setTip({ x: X0 + groupW * (li + 0.5), y: y(v), text: `${s.name} · ${chart.x_labels[li]}: ${v}` })}>
                      <title>{`${s.name} · ${chart.x_labels[li]}: ${v}`}</title>
                    </circle>
                  ))}
                </g>
              ))}
          {chart.x_labels.map((label, li) => (
            <text key={li} x={X0 + groupW * (li + 0.5)} y={H - 8} textAnchor="middle" className="nd-axis">{label}</text>
          ))}
        </svg>
        {tip && <div className="nd-tip" style={{ left: `${(tip.x / W) * 100}%`, top: `${(tip.y / H) * 100}%` }}>{tip.text}</div>}
      </div>
    </figure>
  );
}
